import React, { MutableRefObject, ReactElement, useContext, useRef, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { DirectoryModal, LanguageErrorCodes } from '../../../i18n'
import CLanguageHandler from '../../NonReact/Languages/LanguageHandler'
import { addDirectoryURL, updateDirectoryURL } from '../../NonReact/constants/ApiConstants'
import { EMPTY_STRING } from '../../NonReact/constants/Common'
import { RESTMethods } from '../../NonReact/constants/REST'
import { HIDE } from '../../NonReact/constants/class-names'
import { IsVarEmpty } from '../../NonReact/utils/GlobalFunctions'
import {
    DeepMap,
    FieldError,
    FieldValues,
    FormProvider,
    useForm,
    UseFormMethods
} from '../../Vendor/react-hook-form/dist/index.ie11'
import { RESTAPIError } from '../Common/API'
import { ALL_VALUE } from '../Common/API/ItemsGetters'
import { StatusCodes } from '../../NonReact/common/API/REST/RESTAPIClient'
import { requestJSON } from '../../NonReact/common/API/REST/RESTClient'
// Utilities imports.
import { IconName } from '../Common/Components/Icon'
import Tabs from '../Common/Components/Tabs'
import { Tab } from '../Common/Components/Tabs/Types'
import { ON_CHANGE } from '../Common/Constants/HookFormEventNames'
// Contexts imports.
import { LanguageContext, MessageContext, ReactState } from '../Common/Contexts'
import FooterActions from './Actions/FooterActions'
import { DirectoryModalID } from './Constants/Constants'
import { DirectoryRequestMapper } from './Mappers/DirectoryRequestMapper'
import { ApplicationDirectory, ApplicationDirectoryFormData, FilterValues } from './Types/Types'
import DirectoryDetails from './Views/DirectoryDetails'

/**
 * Component properties type.
 */
type Props = {
    viewOnly?: boolean
    closeModal: () => void
    filters?: FilterValues
    selectedDirectory?: Nullable<ApplicationDirectory>
    refreshAction: () => void
}

/**
 * Configuration tabs container component.
 *
 * @param closeModal Modal close callback.
 * @param selectedDirectory The selected Directory from the legacy Directory list page.
 * @param refresh A function to refresh the list page on submission of the form.
 * @param filters Object containing the filter values for the list page.
 */
const ModifyAddDirectoryTabs: React.FC<Props> = ({
    closeModal,
    selectedDirectory,
    filters,
    refreshAction,
    viewOnly
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(DirectoryModal.NAME)
    language.loadBundle(LanguageErrorCodes.NAME)

    // Context that allows error messages to be displayed on the modal
    const { displayError } = useContext(MessageContext)

    // React Ref to keep track of the modal being in view
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    // If value is null or undefined then return a specified default value, if not then return the value
    const getValueForDefault = <T extends string | number | number[]>(value: Nullable<T>, defaultValue: T): T => {
        if (Array.isArray(value)) {
            return value && value.length === 1 ? value : defaultValue
        }
        return value ?? defaultValue
    }

    const [isSubmitting, setIsSubmitting]: ReactState<boolean> = useState<boolean>(false)
    const [isValidationPopperOpen, setIsValidationPopperOpen]: ReactState<boolean> = useState<boolean>(false)

    // Active tab ID.
    const [activeTabID, setActiveTabID]: ReactState<number> = useState<number>(DirectoryModalID.DirectoryDetails)

    // State to store any form errors
    const [serverError, setServerError]: ReactState<Definable<string>> = useState<string>()

    // Sets the default values for the form within the Modal.
    const defaultValues: MutableRefObject<ApplicationDirectoryFormData> = useRef({
        directoryDetails: {
            telSysID: !IsVarEmpty(selectedDirectory)
                ? selectedDirectory.TelSysID ?? ALL_VALUE
                : getValueForDefault(filters?.telSysID, ALL_VALUE),
            partitionID: !IsVarEmpty(selectedDirectory)
                ? selectedDirectory.PartIDInTelSys ?? ALL_VALUE
                : getValueForDefault(filters?.partitionID, ALL_VALUE),
            telNumber: !IsVarEmpty(selectedDirectory)
                ? getValueForDefault(selectedDirectory?.TelephoneNumber, EMPTY_STRING)
                : EMPTY_STRING,
            description: !IsVarEmpty(selectedDirectory)
                ? getValueForDefault(selectedDirectory?.Description, EMPTY_STRING)
                : EMPTY_STRING
        }
    })
    // Form context.
    const methods: UseFormMethods = useForm({
        defaultValues: defaultValues.current,
        mode: ON_CHANGE,
        reValidateMode: ON_CHANGE,
        shouldUnregister: false
    })

    // eslint-disable-next-line
    const {
        handleSubmit,
        errors,
        formState: { isValid }
    }: UseFormMethods = methods

    /**
     * Tab activation method.
     * @param newActiveTabID ID of tab to activate.
     */
    const activateTab = (newActiveTabID: number): void => {
        setActiveTabID(newActiveTabID)
    }

    const determineValidationErrors = (formErrors: DeepMap<FieldValues, FieldError>): boolean[] => [
        !!formErrors?.directoryDetails
    ]

    const validationErrors: boolean[] = determineValidationErrors(errors)

    // eslint-disable-next-line
    const submitCodesConfiguration = async (state: ApplicationDirectoryFormData): Promise<void> => {
        // Will fire if save action was successful - Zero errors
        const successfulSubmit = (): void => {
            // Close Modal and Refresh the list page.
            closeModal()
            refreshAction()
        }

        // Will fire if save action was not successful - One or more errors
        const failedSubmit = (error: RESTAPIError): void => {
            const { errorCode, errorMessage, HTTPStatus }: RESTAPIError = error

            if (HTTPStatus === StatusCodes.OK || HTTPStatus === StatusCodes.OK_NO_CONTENT) {
                displayError(error)
                return successfulSubmit()
            }

            const errorCodeLanguage: Nullable<string> = (LanguageErrorCodes as unknown as Record<string, string>)[
                `ErrorCode_${errorCode}`
            ]

            setServerError(errorCodeLanguage ? language.get(errorCodeLanguage) : errorMessage)
            setIsSubmitting(false)

            return undefined
        }

        if (
            state.directoryDetails &&
            !IsVarEmpty(state.directoryDetails?.telSysID) &&
            !IsVarEmpty(state.directoryDetails.partitionID)
        ) {
            const {
                directoryDetails: { telSysID, partitionID }
            }: ApplicationDirectoryFormData = state

            if (IsVarEmpty(selectedDirectory)) {
                // The second parameter determines if the telephonyServer and partitionID are included in the request payload

                // Makes a request to the Codes api in the format of JSON
                requestJSON(addDirectoryURL(String(telSysID), String(partitionID)), {
                    method: RESTMethods.POST,
                    body: {
                        TelephoneNumber: state.directoryDetails.telNumber,
                        Description: state.directoryDetails.description
                    } as ApplicationDirectory
                })
                    .then(successfulSubmit)
                    .catch(failedSubmit)
            } else {
                const mappedState: ApplicationDirectory = DirectoryRequestMapper(
                    state,
                    selectedDirectory,
                    RESTMethods.PUT
                )

                requestJSON(
                    updateDirectoryURL(
                        String(selectedDirectory.TelSysID),
                        String(selectedDirectory.PartIDInTelSys),
                        String(selectedDirectory.TelephoneNumber)
                    ),
                    {
                        method: RESTMethods.PUT,
                        body: mappedState
                    }
                )
                    .then(successfulSubmit)
                    .catch(failedSubmit)
            }
        }
    }

    // eslint-disable-next-line
    const invalidFormSubmit = (errors: DeepMap<FieldValues, FieldError>): void => {
        if (!isValid) {
            setActiveTabID(
                determineValidationErrors(errors).findIndex((validationError: boolean): boolean => validationError)
            )
            setIsValidationPopperOpen(true)

            setTimeout((): void => {
                if (isMounted.current) {
                    setIsValidationPopperOpen(false)
                }
            }, 2500)
        }
    }

    // List of all tabs settings.
    const tabs: Nullable<Tab[]> = [
        {
            ID: DirectoryModalID.DirectoryDetails,
            isDisplayed: true,
            tabsMenuItem: {
                title: language.get(DirectoryModal.DirectoryAddModify_TabsDetails),
                iconName: IconName.PIN_NUM_OUTLINED,
                onClick: (): void => activateTab(DirectoryModalID.DirectoryDetails)
            },
            content: <DirectoryDetails filters={filters} viewOnly={!IsVarEmpty(selectedDirectory) || viewOnly} />,
            hasError: validationErrors[DirectoryModalID.DirectoryDetails]
        }
    ]

    return (
        <div className='configuration-tabs'>
            <FormProvider {...methods}>
                <form
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(submitCodesConfiguration, invalidFormSubmit)}
                    className={isSubmitting ? HIDE : EMPTY_STRING}
                    noValidate
                >
                    {tabs && <Tabs tabs={tabs} activeTabID={activeTabID} />}
                    {!viewOnly && (
                        <FooterActions
                            serverError={serverError}
                            isValidationPopperOpen={isValidationPopperOpen}
                            closeModal={closeModal}
                        />
                    )}
                </form>
            </FormProvider>
        </div>
    )
}

export default ModifyAddDirectoryTabs
